import { z } from 'zod';

import { type Translate } from '@shared/hooks/locale';
import { Currencies, type Currency } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

/**
 * Zod schema for `activity.extra_data` on `activity_type = 'change'` rows.
 *
 * Mirror of:
 *  - agg-next/src/schemas/lot-change-extra-data.ts
 *  - realadvisor-app/src/schemas/lot-change-extra-data.ts
 *
 * All three repos write into the same Postgres table, so the contract has
 * to match — keep them in sync whenever one of them changes.
 */

// ----------------------------------------------------------------------------
// Lot statuses
// ----------------------------------------------------------------------------
export const LotStatusSchema = z.enum([
  'draft',
  'pending_review',
  'available',
  'available_coming_soon',
  'available_off_market',
  'paused',
  'rejected',
  'reserved',
  'won',
  'lost_sold_owner',
  'lost_sold_someone',
  'abandoned',
]);
export type LotStatus = z.infer<typeof LotStatusSchema>;

const PLAIN_STATUSES = [
  'draft',
  'pending_review',
  'available',
  'available_coming_soon',
  'available_off_market',
  'reserved',
  'won',
  'lost_sold_owner',
  'lost_sold_someone',
  'abandoned',
] as const;

// ----------------------------------------------------------------------------
// Per-purpose reason-code enums and FSBO contexts
// ----------------------------------------------------------------------------
export const DeactivationReasonCodeSchema = z.enum([
  'sold_or_rented',
  'using_agent',
  'no_longer_selling',
  'needs_update',
  'other',
]);
export type DeactivationReasonCode = z.infer<
  typeof DeactivationReasonCodeSchema
>;

export const RejectionReasonCodeSchema = z.enum([
  'incomplete_photos',
  'price_unrealistic',
  'duplicate_listing',
  'policy_violation',
  // Fraudulent listing (#24256): the reject dialog pairs it with "Block this
  // advertiser", and the block_fsbo_user action writes it on the listings it
  // takes down.
  'scam',
  'other',
]);
export type RejectionReasonCode = z.infer<typeof RejectionReasonCodeSchema>;

export const DeactivationFsboSchema = z.object({
  reason_code: DeactivationReasonCodeSchema,
  /** Set when reason_code === 'sold_or_rented'. */
  sold_or_rented_on_realadvisor: z.boolean().optional(),
});
export type DeactivationFsbo = z.infer<typeof DeactivationFsboSchema>;

export const RejectionFsboSchema = z.object({
  reason_code: RejectionReasonCodeSchema,
  /** Message shown to the seller in the rejection email. */
  seller_message: z.string().optional(),
});
export type RejectionFsbo = z.infer<typeof RejectionFsboSchema>;

// ----------------------------------------------------------------------------
// Field deltas
// ----------------------------------------------------------------------------
export const OfferTypeDeltaSchema = z.object({
  from: z.string().nullable(),
  to: z.string(),
});

export const PriceFieldSchema = z.enum([
  'sale_price',
  'rent_net',
  'rent_extra',
]);
export type PriceField = z.infer<typeof PriceFieldSchema>;

export const PriceDeltaSchema = z.object({
  field: PriceFieldSchema,
  from: z.number(),
  to: z.number(),
  currency: z.string().nullable(),
});

export const PortalDeltaSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  live: z.boolean(),
});

// ----------------------------------------------------------------------------
// Per-purpose extra_data variants
// ----------------------------------------------------------------------------
export const DeactivationExtraDataSchema = z.object({
  status: z.object({
    from: LotStatusSchema.nullable(),
    to: z.literal('paused'),
  }),
  fsbo: DeactivationFsboSchema.optional(),
});
export type DeactivationExtraData = z.infer<typeof DeactivationExtraDataSchema>;

export const RejectionExtraDataSchema = z.object({
  status: z.object({
    from: LotStatusSchema.nullable(),
    to: z.literal('rejected'),
  }),
  fsbo: RejectionFsboSchema.optional(),
});
export type RejectionExtraData = z.infer<typeof RejectionExtraDataSchema>;

export const PlainStatusChangeExtraDataSchema = z.object({
  status: z.object({
    from: LotStatusSchema.nullable(),
    to: z.enum(PLAIN_STATUSES),
  }),
});
export type PlainStatusChangeExtraData = z.infer<
  typeof PlainStatusChangeExtraDataSchema
>;

export const PriceChangeExtraDataSchema = z.object({ price: PriceDeltaSchema });
export type PriceChangeExtraData = z.infer<typeof PriceChangeExtraDataSchema>;

export const OfferTypeChangeExtraDataSchema = z.object({
  offer_type: OfferTypeDeltaSchema,
});
export type OfferTypeChangeExtraData = z.infer<
  typeof OfferTypeChangeExtraDataSchema
>;

export const PortalChangeExtraDataSchema = z.object({
  portal: PortalDeltaSchema,
});
export type PortalChangeExtraData = z.infer<typeof PortalChangeExtraDataSchema>;

// Written by the fsbo-auto-review Cloud Run subscriber when an FSBO listing
// transitions into pending_review. On auto-approve, status flips
// pending_review -> available alongside this activity in one Hasura
// multi-mutation; on hold, status stays pending_review and only the activity
// row is written. The top-level `auto_review` key is the structural
// discriminant — it must be checked before PlainStatusChangeExtraDataSchema
// in the union below or auto_review rows would be classified as plain status
// changes.
export const AutoReviewExtraDataSchema = z.object({
  status: z.object({
    from: z.literal(LotStatusSchema.enum.pending_review),
    to: z.union([
      z.literal(LotStatusSchema.enum.available),
      z.literal(LotStatusSchema.enum.pending_review),
    ]),
  }),
  auto_review: z.object({
    score: z.number(),
    decision: z.enum(['approved', 'held']),
    flags: z.array(z.string()),
    signals: z.record(z.unknown()).optional(),
    model_version: z.string(),
    // Which mode the service ran in (functions/src/fsbo-auto-review/mode.ts).
    // Parsed for completeness only — nothing in the CRM renders it; it exists
    // for the SQL trail (`extra_data->'auto_review'->>'mode'`). Optional: rows
    // written before the mode existed carry neither field.
    mode: z.enum(['enforce', 'shadow']).optional(),
    // Shadow mode only: the verdict the score produced before it was
    // downgraded to a hold. `decision` is always 'held' when this is set.
    shadow_decision: z.enum(['approved', 'held']).optional(),
  }),
});
export type AutoReviewExtraData = z.infer<typeof AutoReviewExtraDataSchema>;

// Written by the block_fsbo_user Hasura action (functions/src/hasura-actions/
// routes/block-fsbo-user) on every listing of a blocked advertiser it acts
// on (#24256). A listing that was live carries the status delta back to
// pending_review; one already waiting in pending_review carries no delta,
// only the annotation, so the listing page can say why it is sitting there.
// The internal note travels on activity.note. The top-level `fsbo_block` key
// is the structural discriminant — before PlainStatusChangeExtraDataSchema
// in the union, or the live → pending_review rows would read as a plain
// "submitted for review". CRM-only shape: the seller apps' copies of this
// schema fall back to null for it, which is what a shadow ban wants.
export const FsboBlockExtraDataSchema = z.object({
  status: z
    .object({
      from: LotStatusSchema.nullable(),
      to: z.literal(LotStatusSchema.enum.pending_review),
    })
    .optional(),
  fsbo_block: z.object({
    /** The blocked advertiser (users.id). */
    user_id: z.string(),
    /** The block batch (users.fsbo_block_batch_id) the account joined. */
    batch_id: z.string().optional(),
  }),
});
export type FsboBlockExtraData = z.infer<typeof FsboBlockExtraDataSchema>;

// Written by unblock_fsbo_users on every listing the block was holding: the
// users columns are nulled by the unblock, so this row is what remembers
// that the account was blocked and cleared, by whom and when. No status
// delta — a reviewer approves the listing by hand.
export const FsboUnblockExtraDataSchema = z.object({
  fsbo_unblock: z.object({
    /** The advertiser that was unblocked (users.id). */
    user_id: z.string(),
    /** The batch the account was in when blocked, if any. */
    batch_id: z.string().nullable().optional(),
  }),
});
export type FsboUnblockExtraData = z.infer<typeof FsboUnblockExtraDataSchema>;

export const ChangeExtraDataSchema = z.union([
  DeactivationExtraDataSchema,
  RejectionExtraDataSchema,
  AutoReviewExtraDataSchema,
  FsboBlockExtraDataSchema,
  FsboUnblockExtraDataSchema,
  PlainStatusChangeExtraDataSchema,
  PriceChangeExtraDataSchema,
  OfferTypeChangeExtraDataSchema,
  PortalChangeExtraDataSchema,
]);
export type ChangeExtraData = z.infer<typeof ChangeExtraDataSchema>;

// Memoize parses keyed by the raw extra_data object reference. The same
// activity row is consumed by multiple feed components (ActivityItem for
// icon picking, ActivityTitle for the headline, ChangeActivityItemContent
// for the body), and re-running zod for each is wasted work. Apollo cache
// keeps the same object reference for the same row, so a WeakMap hits on
// subsequent reads and dies on its own when the row is evicted.
const parseCache = new WeakMap<object, ChangeExtraData | null>();
export const parseChangeExtraData = (raw: unknown): ChangeExtraData | null => {
  if (typeof raw !== 'object' || raw == null) {
    return null;
  }
  const cached = parseCache.get(raw);
  if (cached !== undefined) {
    return cached;
  }
  const result = ChangeExtraDataSchema.safeParse(raw);
  const parsed = result.success ? result.data : null;
  parseCache.set(raw, parsed);
  return parsed;
};

export const parseLotStatus = (raw: unknown): LotStatus | null => {
  const result = LotStatusSchema.safeParse(raw);
  return result.success ? result.data : null;
};

// `extra.price.currency` is `z.string().nullable()` (the schema accepts any
// string for historical compatibility and cross-repo parity). Render sites
// pass the resolved value to Intl `toLocaleString({ style: 'currency' })`,
// which throws RangeError on unknown codes — guard the cast here so an
// out-of-set currency falls back to CHF instead of breaking the title.
export const coerceCurrency = (raw: string | null | undefined): Currency => {
  if (raw != null && (Currencies as readonly string[]).includes(raw)) {
    return raw as Currency;
  }
  return 'CHF';
};

// FSBO reason-code label map for the deactivation/rejection chip. Typed as
// Record<union, string> so adding a new value to DeactivationReasonCodeSchema
// or RejectionReasonCodeSchema becomes a compile error here until a
// translation is added — avoids silently rendering the raw enum key in
// non-English locales. Shared with the legacy feed renderer.
export type LotChangeReasonCode = DeactivationReasonCode | RejectionReasonCode;

export const formatReasonCode = (
  code: LotChangeReasonCode,
  t: Translate,
): string => {
  const labels: Record<LotChangeReasonCode, string> = {
    // FSBO rejection reasons
    incomplete_photos: t('Incomplete photos'),
    price_unrealistic: t('Price unrealistic'),
    duplicate_listing: t('Duplicate listing'),
    policy_violation: t('Policy violation'),
    scam: t('Scam or fraud'),
    // FSBO deactivation reasons
    sold_or_rented: t('Sold or rented'),
    using_agent: t('Using an agent'),
    no_longer_selling: t('No longer selling'),
    needs_update: t('Needs update'),
    // shared
    other: t('Other'),
  };
  return labels[code];
};

// One row of the auto-review breakdown, rendered as "Label: value" with a
// status icon. `pass` = the check is satisfied (green tick); `fail` = the
// check is a blocker or scored nothing (red warning).
export type AutoReviewBreakdownRow = {
  label: string;
  value: string;
  status: 'pass' | 'fail';
};

// Safe readers for the loosely-typed per-check `signals` payload.
const autoReviewSignal = (
  signals: Record<string, unknown> | undefined,
  key: string,
): Record<string, unknown> | null => {
  const value = signals?.[key];
  return typeof value === 'object' && value != null
    ? (value as Record<string, unknown>)
    : null;
};
const asStr = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;
const asNum = (value: unknown): number | null =>
  typeof value === 'number' ? value : null;

const autoReviewTierLabel = (tier: string | null, t: Translate): string => {
  if (tier === 'ch') {
    return t('Switzerland');
  }
  if (tier === 'trusted') {
    return t('Trusted country');
  }
  if (tier === 'neutral') {
    return t('Other country');
  }
  if (tier === 'high_risk') {
    return t('High-risk country');
  }
  return t('Unknown');
};

// What the reviewer decided about the submission's network (model v6,
// #24256): `network_tier` on the ip signal is the `asns` table's verdict for
// the Cloudflare ASN. Only `ch_consumer` passes; the other two hold.
// `ch_consumer` covers any Swiss access network that is not hosting — a
// consumer or mobile ISP, but also a company, a university or a public body
// — hence the label.
// ipinfo's classification of the autonomous system (asns.type, echoed as
// `network_type` on the ip signal). Shown next to the verdict so a reviewer
// sees WHY a Swiss network passed (an ISP, a company, a university) or held.
const autoReviewNetworkTypeLabel = (
  type: string,
  t: Translate,
): string | null => {
  switch (type) {
    case 'isp':
      return t('ISP');
    case 'business':
      return t('business network');
    case 'education':
      return t('education network');
    case 'government':
      return t('government network');
    case 'hosting':
      return t('hosting');
    default:
      return null;
  }
};

const autoReviewNetworkTierLabel = (tier: string, t: Translate): string => {
  if (tier === 'ch_consumer') {
    return t('Swiss network');
  }
  if (tier === 'hosting') {
    return t('Hosting / VPN network');
  }
  return t('Network not classified yet');
};

// Network origin of the submission IP (Cloudflare ASN + organisation),
// recorded on the ip signal since #24256. Before model v6 this was
// informational; since v6 the tier next to it (autoReviewNetworkTierLabel)
// is what passed or held the listing.
const autoReviewNetworkLabel = (
  asn: number | null,
  organisation: string | null,
): string | null => {
  if (organisation != null && asn != null) {
    return `${organisation} (AS${asn})`;
  }
  if (organisation != null) {
    return organisation;
  }
  if (asn != null) {
    return `AS${asn}`;
  }
  return null;
};

const autoReviewPriceSource = (source: string | null, t: Translate): string => {
  if (source === 'avm') {
    return t('AVM estimate');
  }
  if (source === 'cma_listings') {
    return t('comparable sales');
  }
  if (source === 'post_merged_listings') {
    return t('comparable rentals');
  }
  if (source === 'online_listings_room') {
    return t('canton room rentals');
  }
  return t('reference');
};

// Labels for the short-circuit hold reasons written by the fsbo-auto-review
// handler (`holdReason`). Unknown reasons fall through to the raw string
// rather than an empty cell — a new backend reason should still be readable
// in the feed before the CRM catches up.
const autoReviewHoldReasonLabel = (reason: string, t: Translate): string => {
  switch (reason) {
    case 'previously_rejected':
      return t('A reviewer rejected this listing before — not auto-reviewed');
    case 'missing_user':
      return t('No seller account on the submission');
    case 'fsbo_user_blocked':
      return t('The advertiser is blocked — not auto-reviewed');
    case 'fsbo_cluster_blocked':
      return t(
        'Shares a name, address or browser with a blocked advertiser — held for review',
      );
    case 'fsbo_auto_blocked':
      return t('Blocked automatically — same browser as a blocked advertiser');
    default:
      return reason;
  }
};

// Full, human-readable breakdown of an auto_review decision, returned as
// Label: value rows so the feed can render it consistently with the other
// change-activity fields (no chips). Reads the per-check `signals` payload so
// a reviewer sees exactly what each check found and why a held listing fell
// short. Flag identifiers from the service stay stable for downstream
// filtering; this is the human-facing view of the same decision.
export const formatAutoReviewBreakdown = (
  autoReview: AutoReviewExtraData['auto_review'],
  t: Translate,
  locale: Parameters<typeof formatPrice>[1],
): AutoReviewBreakdownRow[] => {
  const rows: AutoReviewBreakdownRow[] = [];
  const signals = autoReview.signals;
  // FSBO is CH-only, so prices are CHF — format them like the rest of the CRM.
  const fmt = (n: number): string => formatPrice(n, locale, 'CHF');

  // In shadow mode the score's own verdict lives in `shadow_decision`; the
  // recorded `decision` is always 'held'. Grade the score by what it earned,
  // not by the mode, so a clean 100/100 shadow hold doesn't show a red
  // warning next to the number.
  const shadowDecision = autoReview.shadow_decision;
  const scoreVerdict = shadowDecision ?? autoReview.decision;
  rows.push({
    label: t('Score'),
    value: t('{{score}} / 100', { score: autoReview.score }),
    status: scoreVerdict === 'approved' ? 'pass' : 'fail',
  });

  // Shadow mode: the listing was held regardless of the score. Say what the
  // scorer would have done so the reviewer can tell "bot wanted to approve
  // this, a human just has to confirm" from "bot found a problem".
  if (shadowDecision != null) {
    rows.push({
      label: t('Auto-approval'),
      value:
        shadowDecision === 'approved'
          ? t('Off (shadow mode) — would have been auto-approved')
          : t('Off (shadow mode) — would have been held anyway'),
      status: shadowDecision === 'approved' ? 'pass' : 'fail',
    });
  }

  const phone = autoReviewSignal(signals, 'phone');
  if (phone != null) {
    const reason = asStr(phone['reason']);
    const tier = asStr(phone['tier']);
    // +41 77 is the virtual-operator range (model v6, #24256): a verified
    // number, but one a one-time-number marketplace sells, so it holds.
    const mvno = phone['mvno'] === true;
    rows.push({
      label: t('Phone'),
      value:
        reason === 'no_verified_phone'
          ? t('No verified phone')
          : reason === 'unparseable_phone'
          ? t('Number not recognised')
          : `${asStr(phone['country']) ?? '—'} · ${autoReviewTierLabel(
              tier,
              t,
            )}${mvno ? ` · ${t('+41 77 virtual-operator range')}` : ''}`,
      // Phone reflects the owner's residence: CH and trusted countries pass,
      // unless the number sits in the held range.
      status: !mvno && (tier === 'ch' || tier === 'trusted') ? 'pass' : 'fail',
    });
  }

  const ip = autoReviewSignal(signals, 'ip');
  if (ip != null) {
    const reason = asStr(ip['reason']);
    const tier = asStr(ip['tier']);
    const location =
      reason === 'no_marketing_metadata'
        ? t('No location data')
        : `${asStr(ip['country']) ?? '—'} · ${autoReviewTierLabel(tier, t)}`;
    const network = autoReviewNetworkLabel(
      asNum(ip['asn']),
      asStr(ip['as_organization']) ?? asStr(ip['network_name']),
    );
    // Model v6 rows carry the network verdict; older rows do not, and for
    // them the country tier is still what decided.
    const networkTier = asStr(ip['network_tier']);
    const networkType = asStr(ip['network_type']);
    const networkTypeLabel =
      networkType != null ? autoReviewNetworkTypeLabel(networkType, t) : null;
    const networkVerdict =
      networkTier == null
        ? null
        : autoReview.flags.includes('ip_asn_missing')
        ? t('No network data')
        : `${autoReviewNetworkTierLabel(networkTier, t)}${
            networkTypeLabel != null ? `, ${networkTypeLabel}` : ''
          }`;
    const parts = [location, network, networkVerdict].filter(
      (part): part is string => part != null,
    );
    rows.push({
      label: t('Submission location (IP)'),
      value: parts.join(' · '),
      // Since model v6 the IP is a gate: only a CH IP on a Swiss consumer
      // network passes, and every other outcome carries an `ip_*` flag —
      // including `ip_not_ch`, recorded with no network verdict since the
      // network is only consulted for CH. Rows from before v6 carry neither
      // flag nor verdict and are graded the way they were decided: CH and
      // trusted countries passed.
      status: autoReview.flags.some(flag => flag.startsWith('ip_'))
        ? 'fail'
        : networkTier != null
        ? tier === 'ch' && networkTier === 'ch_consumer'
          ? 'pass'
          : 'fail'
        : tier === 'ch' || tier === 'trusted'
        ? 'pass'
        : 'fail',
    });
  }

  const price = autoReviewSignal(signals, 'price');
  if (price != null) {
    const reason = asStr(price['reason']);
    const asking = asNum(price['asking']);
    const ref = asNum(price['reference_value']);
    const tooLow = autoReview.flags.includes('price_too_low');
    // Single-room sublets are priced against the canton's room market (the
    // median asking rent of active room listings) rather than a whole-unit
    // valuation; `single_room_unverifiable` means the canton had too few room
    // listings to trust.
    const sampleSize = asNum(price['sample_size']);
    let value: string;
    if (reason === 'no_asking_price') {
      value = t('No asking price');
    } else if (reason === 'no_reference') {
      value = t('No market reference');
    } else if (reason === 'single_room_unverifiable') {
      value =
        sampleSize != null && sampleSize > 0
          ? t('Not enough comparable rooms in the canton ({{count}})', {
              count: sampleSize,
            })
          : t('No comparable rooms in the canton');
    } else if (asking != null && ref != null) {
      value = t('{{status}} — asking {{asking}}, reference {{ref}} ({{src}})', {
        status: tooLow ? t('Below market') : t('In line with market'),
        asking: fmt(asking),
        ref: fmt(ref),
        src: autoReviewPriceSource(asStr(price['reference_source']), t),
      });
    } else {
      value = t('Could not be valued');
    }
    rows.push({
      label: t('Price'),
      value,
      status: !tooLow && asking != null && ref != null ? 'pass' : 'fail',
    });
  }

  const description = autoReviewSignal(signals, 'description');
  if (description != null) {
    const reason = asStr(description['reason']);
    const hasContact = description['has_contact'] === true;
    const isSpam = description['is_spam'] === true;
    // A non-empty unknown_phones means the contact was a phone number that
    // isn't the seller's own / dummy contact — surface that specifically.
    const unknownPhones = description['unknown_phones'];
    const hasUnknownPhone =
      Array.isArray(unknownPhones) && unknownPhones.length > 0;
    let value: string;
    if (reason === 'empty_description') {
      value = t('No description');
    } else if (reason != null && reason !== '') {
      value = t('Not analysed');
    } else if (hasUnknownPhone) {
      value = isSpam
        ? t('Phone number not matching the listing contact, and spam pattern')
        : t('Phone number not matching the listing contact');
    } else if (hasContact && isSpam) {
      value = t('Contact info and spam pattern');
    } else if (hasContact) {
      value = t('Contact info in text');
    } else if (isSpam) {
      value = t('Spam pattern');
    } else {
      value = t('Clean');
    }
    rows.push({
      label: t('Description'),
      value,
      status: reason == null && !hasContact && !isSpam ? 'pass' : 'fail',
    });
  }

  // Short-circuit holds never run the four checks, so they carry a bare
  // top-level `{ reason }` instead of the per-signal payloads above and would
  // otherwise render as a lone "Score 0 / 100" row with no explanation.
  // Surface the reason so a reviewer can tell "we deliberately didn't score
  // this" apart from "it scored zero". A scored decision has no top-level
  // `reason` (each check's own reason lives inside its signal), so this row
  // only ever appears for a short-circuit.
  const holdReason = asStr(signals?.['reason']);
  if (holdReason != null) {
    rows.push({
      label: t('Reason'),
      value: autoReviewHoldReasonLabel(holdReason, t),
      status: 'fail',
    });
  }

  return rows;
};
